import { useEffect, useState } from 'react';
import Icon from '../app/components/Icon';
import Sheet from '../app/components/Sheet';
import DateField from '../app/components/DateField';
import { AnnouncementCard } from '../app/components/Announcements';
import { ANCHORS, APP_VERSION, AUDIENCES, KINDS, LINKS, SCREENS } from '../app/lib/announcements';
import { addDays, shortDate, today } from '../app/lib/dates';
import { useApp } from '../app/lib/AppContext';

const INPUT = 'input h-11 w-full rounded-[14px] border-line text-base';
const SELECT = 'select h-11 w-full rounded-[14px] border-line text-base';
const LABEL = 'text-[15px] font-semibold';
const HINT = 'text-xs text-muted';
const BUTTON = 'btn h-11 rounded-[14px] border-line bg-base-100 px-4';
// "Có hạn dùng": a new announcement runs 30 days from today unless changed.
const RUN_DAYS = 30;

const kindLabel = (id) => KINDS.find((k) => k.id === id)?.label || id;
const placeLabel = (a) => (a.kind === 'whats_new' ? 'Trang chủ'
  : (a.kind === 'banner' ? SCREENS.find((s) => s.id === a.placement)?.label : ANCHORS.find((x) => x.id === a.placement)?.label) || a.placement);
const date = (iso) => `${shortDate(iso)}/${iso.slice(2, 4)}`;

function status(a) {
  const todayIso = today();
  if (!a.active) return { label: 'Đang tắt', chip: 'bg-base-300 text-base-content' };
  if (a.starts_on && a.starts_on > todayIso) return { label: 'Chưa bắt đầu', chip: 'bg-[#fdf3d4] text-[#8a6100]' };
  if (a.last_day && a.last_day < todayIso) return { label: 'Đã hết hạn', chip: 'bg-base-300 text-muted' };
  return { label: 'Đang hiện', chip: 'bg-[#e3f1e9] text-[#17804f]' };
}

const VIA_LABELS = [
  ['got_it', 'Bấm nút'], ['feature', 'Chạm tính năng'], ['cta', 'Thử ngay'], ['outside', 'Chạm ra ngoài'], ['close', 'Đóng'],
];

function Stats({ stats }) {
  const seenAfterShown = VIA_LABELS.reduce((sum, [via]) => sum + (stats.via[via] || 0), 0);
  const rate = seenAfterShown ? Math.round((stats.used_after / seenAfterShown) * 100) : null;
  return (
    <div className="flex flex-col gap-1.5 rounded-[14px] bg-base-200 p-3 text-sm">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span><b className="tabular">{stats.people}</b> người đã thấy <span className="text-muted">({stats.shows} lần hiện)</span></span>
        {rate != null && <span><b className="tabular">{rate}%</b> dùng tính năng trong 7 ngày sau khi xem</span>}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted">
        {VIA_LABELS.map(([via, label]) => (stats.via[via] ? <span key={via}>{label}: <b className="tabular text-base-content">{stats.via[via]}</b></span> : null))}
        {stats.found_first > 0 && <span>Tự tìm ra trước khi thấy: <b className="tabular text-base-content">{stats.found_first}</b></span>}
        {stats.people === 0 && stats.found_first === 0 && <span>Chưa có ai thấy.</span>}
      </div>
    </div>
  );
}

function AnnouncementRow({ announcement: a, onEdit, onToggle, onPreview }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const s = status(a);
  const run = async (fn) => {
    setBusy(true);
    setNote(null);
    try {
      setNote(await fn());
    } catch (e) {
      setNote(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="flex flex-col gap-3 rounded-[20px] bg-base-100 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="font-bold">{a.title}</h2>
          <p className="text-xs text-muted"><code>{a.key}</code> · {kindLabel(a.kind)}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${s.chip}`}>{s.label}</span>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted">Vị trí</dt><dd>{placeLabel(a)}</dd>
        <dt className="text-muted">Thời gian</dt>
        <dd className="tabular">{a.starts_on ? date(a.starts_on) : 'Ngay khi bật'} – {a.last_day ? date(a.last_day) : 'không hết hạn'}</dd>
        <dt className="text-muted">Đối tượng</dt>
        <dd>{AUDIENCES.find((x) => x.id === a.audience)?.label}{a.min_app_version ? ` · app từ ${a.min_app_version}` : ''} · ưu tiên {a.priority}</dd>
      </dl>
      <Stats stats={a.stats} />
      {note && <p role="status" className="text-sm text-muted">{note}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className={BUTTON} onClick={onEdit}><Icon name="pencil" className="size-4" /> Sửa</button>
        <button type="button" className={BUTTON} disabled={busy} onClick={() => run(onToggle)}>{a.active ? 'Tắt' : 'Bật'}</button>
        <button type="button" className={BUTTON} disabled={busy} onClick={() => run(onPreview)}><Icon name="eye" className="size-4" /> Xem thử</button>
      </div>
    </article>
  );
}

const blank = () => ({
  key: '', kind: 'tooltip', placement: ANCHORS[0].id, title: 'Mới: ', body: '', button_label: '', link_path: '',
  items: [{ title: '', body: '', link_path: '' }], min_app_version: '', starts_on: today(), ends_on: addDays(today(), RUN_DAYS),
  audience: 'all', priority: 0, active: false,
});

// Everything an announcement is made of (section 4 of the spec), with a live preview of a tooltip.
function AnnouncementForm({ initial, onSave, onDelete }) {
  const [form, setForm] = useState(() => ({
    ...blank(), ...initial,
    items: initial?.items?.length ? initial.items : blank().items,
    body: initial?.body || '', button_label: initial?.button_label || '', link_path: initial?.link_path || '',
    min_app_version: initial?.min_app_version || '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));
  const input = (field) => ({ value: form[field] ?? '', onChange: (e) => set(field)(e.target.value) });
  const setItem = (i, field, value) => setForm((f) => ({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [field]: value } : it)) }));

  // Each kind points at its own sort of place.
  const places = form.kind === 'banner' ? SCREENS : form.kind === 'dot' ? ANCHORS.filter((x) => x.dot) : ANCHORS;
  const setKind = (kind) => setForm((f) => {
    const options = kind === 'banner' ? SCREENS : kind === 'dot' ? ANCHORS.filter((x) => x.dot) : ANCHORS;
    return { ...f, kind, placement: options.some((o) => o.id === f.placement) ? f.placement : options[0]?.id || '' };
  });

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSave({
        ...form,
        placement: form.kind === 'whats_new' ? null : form.placement,
        items: form.kind === 'whats_new' ? form.items.filter((it) => it.title.trim() || it.body.trim()) : [],
        link_path: form.kind === 'banner' ? form.link_path : '',
      });
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="flex flex-col gap-5">
      <fieldset className="flex flex-col gap-2">
        <legend className={`${LABEL} mb-2`}>Hình thức</legend>
        {KINDS.map((k) => (
          <label key={k.id} className={`flex cursor-pointer gap-3 rounded-[14px] border p-3 ${form.kind === k.id ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
            <input type="radio" name="kind" className="radio radio-sm mt-0.5" checked={form.kind === k.id} onChange={() => setKind(k.id)} />
            <span><span className="block font-semibold">{k.label}</span><span className={HINT}>{k.hint}</span></span>
          </label>
        ))}
      </fieldset>

      {form.kind === 'whats_new' ? (
        <p className={HINT}>Hiện ở Trang chủ, một lần, cho người đã dùng app từ trước ngày bắt đầu.</p>
      ) : (
        <label className="flex flex-col gap-2">
          <span className={LABEL}>{form.kind === 'banner' ? 'Màn hình' : 'Nút được chỉ vào'}</span>
          <select className={SELECT} value={form.placement || ''} onChange={(e) => set('placement')(e.target.value)}>
            {places.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          {form.kind !== 'banner' && <span className={HINT}>Cần nút mới? Gắn mã nút trong app (lib/announcements.js) trước.</span>}
        </label>
      )}

      <label className="flex flex-col gap-2">
        <span className={LABEL}>Tiêu đề</span>
        <input className={INPUT} maxLength={80} required {...input('title')} />
        <span className={HINT}>Bắt đầu bằng "Mới:". Ví dụ: Mới: Sắp xếp tuần</span>
      </label>
      <label className="flex flex-col gap-2">
        <span className={LABEL}>Nội dung <span className="font-normal text-muted">({form.body.length}/200)</span></span>
        <textarea className="textarea min-h-20 w-full rounded-[14px] border-line text-base" maxLength={200} {...input('body')} />
        <span className={HINT}>Tối đa khoảng 2 dòng. Nói lợi ích, không mô tả nút.</span>
      </label>

      {form.kind === 'whats_new' && (
        <fieldset className="flex flex-col gap-3">
          <legend className={`${LABEL} mb-2`}>Các tính năng (tối đa 3)</legend>
          {form.items.map((item, i) => (
            <div key={i} className="flex flex-col gap-2 rounded-[14px] border border-line p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">Mục {i + 1}</span>
                {form.items.length > 1 && (
                  <button type="button" className="btn btn-ghost btn-xs" onClick={() => set('items')(form.items.filter((_, j) => j !== i))}>Bỏ</button>
                )}
              </div>
              <input className={INPUT} placeholder="Tên tính năng" aria-label={`Mục ${i + 1}: tên`} value={item.title} onChange={(e) => setItem(i, 'title', e.target.value)} />
              <input className={INPUT} placeholder="Một câu về lợi ích" aria-label={`Mục ${i + 1}: mô tả`} value={item.body} onChange={(e) => setItem(i, 'body', e.target.value)} />
              <select className={SELECT} aria-label={`Mục ${i + 1}: nút Thử ngay`} value={item.link_path || ''} onChange={(e) => setItem(i, 'link_path', e.target.value)}>
                <option value="">Không có nút "Thử ngay"</option>
                {LINKS.map((l) => <option key={l.path} value={l.path}>Thử ngay → {l.label}</option>)}
              </select>
            </div>
          ))}
          {form.items.length < 3 && (
            <button type="button" className={`${BUTTON} w-fit`} onClick={() => set('items')([...form.items, { title: '', body: '', link_path: '' }])}>
              <Icon name="plus" className="size-4" /> Thêm mục
            </button>
          )}
        </fieldset>
      )}

      {form.kind !== 'dot' && (
        <label className="flex flex-col gap-2">
          <span className={LABEL}>Chữ trên nút</span>
          <input className={INPUT} maxLength={30} placeholder={form.kind === 'banner' ? 'Thử ngay' : 'Đã hiểu'} {...input('button_label')} />
        </label>
      )}
      {form.kind === 'banner' && (
        <label className="flex flex-col gap-2">
          <span className={LABEL}>Nút dẫn tới</span>
          <select className={SELECT} {...input('link_path')}>
            <option value="">Không có nút (chỉ có ✕)</option>
            {LINKS.map((l) => <option key={l.path} value={l.path}>{l.label}</option>)}
          </select>
        </label>
      )}

      {form.kind === 'tooltip' && (
        <div className="flex flex-col gap-2">
          <span className={LABEL}>Xem trước</span>
          <div className="rounded-[16px] bg-base-200 p-4"><AnnouncementCard title={form.title} body={form.body} buttonLabel={form.button_label} /></div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-2">
          <label htmlFor="announce-start" className={LABEL}>Bắt đầu</label>
          <DateField id="announce-start" title="Ngày bắt đầu" value={form.starts_on} onChange={(iso) => setForm((f) => ({
            ...f, starts_on: iso, ends_on: f.ends_on && f.ends_on >= iso ? f.ends_on : addDays(iso, RUN_DAYS),
          }))} />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="announce-end" className={LABEL}>Hết hạn</label>
          <DateField id="announce-end" title="Ngày hết hạn" value={form.ends_on} min={form.starts_on || undefined} onChange={set('ends_on')} />
        </div>
      </div>
      {form.kind === 'dot' && <p className={`${HINT} -mt-3`}>Nhãn "Mới" tự mất sau 14 ngày kể từ ngày bắt đầu.</p>}

      <label className="flex flex-col gap-2">
        <span className={LABEL}>Đối tượng</span>
        <select className={SELECT} {...input('audience')}>
          {AUDIENCES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-2">
          <span className={LABEL}>Độ ưu tiên</span>
          <input className={INPUT} type="number" inputMode="numeric" value={form.priority} onChange={(e) => set('priority')(e.target.value)} />
          <span className={HINT}>Số lớn hiện trước</span>
        </label>
        <label className="flex flex-col gap-2">
          <span className={LABEL}>App tối thiểu</span>
          <input className={INPUT} inputMode="decimal" placeholder="VD: 1.1" {...input('min_app_version')} />
          <span className={HINT}>Chỉ áp dụng cho app iOS{APP_VERSION ? ` (máy này: ${APP_VERSION})` : ''}</span>
        </label>
      </div>
      <label className="flex flex-col gap-2">
        <span className={LABEL}>Mã thông báo</span>
        <input className={INPUT} required pattern="[a-z0-9_]+" placeholder="vd: weekly_arrange_v1" autoCapitalize="none" autoCorrect="off" {...input('key')} />
        <span className={HINT}>Chữ thường không dấu, số và _. Đổi mã là thành thông báo mới: ai đã xem sẽ thấy lại.</span>
      </label>
      <label className="flex min-h-11 items-center justify-between gap-3">
        <span className={LABEL}>Bật thông báo</span>
        <input type="checkbox" className="toggle toggle-lg border-base-300 bg-base-300 text-white checked:border-primary checked:bg-primary checked:text-white"
          checked={form.active} onChange={(e) => set('active')(e.target.checked)} />
      </label>

      {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
      <button type="submit" className="btn btn-primary h-12 rounded-[14px] border-0 font-bold" disabled={busy}>
        {busy && <span className="loading loading-spinner loading-sm" />} Lưu
      </button>
      {onDelete && (confirmDelete ? (
        <div className="flex flex-col gap-2 rounded-[14px] bg-[#fdebe4] p-3 text-sm text-[#8e3414]">
          Xoá hẳn thông báo này và số liệu của nó? Muốn ngừng hiện thì chỉ cần tắt.
          <div className="flex gap-2">
            <button type="button" className="btn btn-sm h-9 border-0 bg-[#c2410c] text-white" onClick={onDelete}>Xoá</button>
            <button type="button" className="btn btn-sm h-9" onClick={() => setConfirmDelete(false)}>Huỷ</button>
          </div>
        </div>
      ) : (
        <button type="button" className="btn btn-ghost h-11 text-error" onClick={() => setConfirmDelete(true)}>
          <Icon name="trash" className="size-4" /> Xoá thông báo
        </button>
      ))}
    </form>
  );
}

// Admin panel for announcements of new features: write one, schedule it, turn it on, and see how it did.
// The server checks the admin session; this page only hides itself.
export default function AnnouncementsPage() {
  const { api, admin } = useApp();
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | announcement

  const load = () => api.get('/admin/announcements').then((res) => setList(res.announcements)).catch((e) => setError(e.message));
  useEffect(() => { if (admin) load(); }, [admin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!admin) return <p className="rounded-[20px] bg-base-100 p-5 text-sm">Trang này chỉ dành cho quản trị viên.</p>;

  const save = async (attrs) => {
    if (editing === 'new') await api.post('/admin/announcements', attrs);
    else await api.patch(`/admin/announcements/${editing.id}`, attrs);
    setEditing(null);
    await load();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-[22px] font-bold">Thông báo tính năng mới</h1>
        <button type="button" className="btn btn-primary h-11 shrink-0 rounded-[14px] border-0 px-4 font-bold" onClick={() => setEditing('new')}>
          <Icon name="plus" className="size-5" /> Tạo
        </button>
      </div>
      <p className="text-sm text-muted">
        Mỗi người thấy mỗi thông báo một lần, tối đa một thông báo mỗi lần mở app, không bao giờ đè lên sheet hay hộp thoại.
        Thay đổi có hiệu lực ngay, không cần phát hành bản mới.
      </p>

      {error && <div role="alert" className="rounded-[20px] bg-[#fdebe4] p-4 text-sm text-[#8e3414]">{error}</div>}
      {!list && !error && <div className="skeleton h-48 rounded-[20px]" />}
      {list?.length === 0 && (
        <p className="rounded-[20px] border border-dashed border-line p-5 text-center text-sm text-muted">Chưa có thông báo nào.</p>
      )}
      {list?.map((a) => (
        <AnnouncementRow key={a.id} announcement={a} onEdit={() => setEditing(a)}
          onToggle={async () => {
            const updated = await api.patch(`/admin/announcements/${a.id}`, { active: !a.active });
            setList((all) => all.map((x) => (x.id === a.id ? updated : x)));
            return updated.active ? 'Đã bật.' : 'Đã tắt.';
          }}
          onPreview={async () => {
            await api.post(`/admin/announcements/${a.id}/preview`);
            if (!a.active) return 'Đã đặt lại cho tài khoản của bạn. Bật thông báo để thấy nó trong app.';
            return `Đã đặt lại cho bạn: mở lại app rồi vào ${placeLabel(a)} để xem.`;
          }} />
      ))}

      <Sheet open={!!editing} onClose={() => setEditing(null)} title={editing === 'new' ? 'Tạo thông báo' : 'Sửa thông báo'}>
        {editing && (
          <AnnouncementForm key={editing === 'new' ? 'new' : editing.id} initial={editing === 'new' ? null : editing}
            onSave={save}
            onDelete={editing === 'new' ? null : async () => {
              await api.del(`/admin/announcements/${editing.id}`);
              setEditing(null);
              await load();
            }} />
        )}
      </Sheet>
    </div>
  );
}
